import React, { useState, useEffect } from 'react';
import { useDataCache } from '../contexts/DataCacheContext';
import bookingService from '../services/bookingService';
import { useToast } from './Toast';
import StaffScheduleModal from './StaffScheduleModal';

function getFlagEmoji(countryCode) {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

function StaffManager({ cachedStaff }) {
  const { cache } = useDataCache();
  const [staff, setStaff] = useState(cachedStaff || cache.bookings?.staff || []);
  const [loading, setLoading] = useState(!cachedStaff && !cache.bookings?.staff);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [scheduleModalStaff, setScheduleModalStaff] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (cachedStaff) {
      setStaff(cachedStaff);
      setLoading(false);
    } else if (cache.bookings?.staff) {
      setStaff(cache.bookings.staff);
      setLoading(false);
    } else {
      loadStaff();
    }
  }, [cachedStaff, cache.bookings]);

  const loadStaff = async () => {
    try {
      const data = await bookingService.listStaff();
      setStaff(data);
    } catch (error) {
      console.error('Failed to load staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      const payload = {
        ...formData,
        specialties: formData.specialties ? formData.specialties.split(',').map(s => s.trim()) : []
      };

      if (editingId) {
        await bookingService.updateStaff(editingId, payload);
      } else {
        await bookingService.createStaff(payload);
      }

      setEditingId(null);
      setShowAddForm(false);
      loadStaff();
      showToast('Saved successfully!');
    } catch (error) {
      showToast('Failed to save', 'error');
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('Permanently delete this staff member? This cannot be undone.')) return;
    try {
      await bookingService.deleteStaff(staffId);
      loadStaff();
      showToast('Deleted successfully!');
    } catch (error) {
      showToast('Failed to delete', 'error');
    }
  };

  const exportCSV = async () => {
    try {
      const csv = await bookingService.exportStaffCSV();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'staff.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Exported successfully!');
    } catch (error) {
      showToast('Failed to export', 'error');
    }
  };

  const importCSV = async (file) => {
    const confirmed = window.confirm(
      'Import will add new staff and update existing ones.\n\n' +
      'How it works:\n' +
      '• Leave staff_id blank for new staff (ID auto-generated)\n' +
      '• Keep staff_id to update existing staff\n' +
      '• Staff not in CSV will NOT be deleted\n\n' +
      'To delete staff, use the delete button.\n\n' +
      'Continue with import?'
    );
    
    if (!confirmed) return;
    
    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
      const result = await bookingService.importStaffCSV(text);
      loadStaff();
      showToast(`Imported: ${result.created} created, ${result.updated} updated`);
      if (result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }
    } catch (error) {
      showToast('Failed to import', 'error');
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      <div className="flex justify-end mb-4 gap-2">
        <button
          onClick={exportCSV}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm font-bold"
        >
          Export CSV
        </button>
        <label className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm font-bold cursor-pointer" title="Import adds/updates staff. Does not delete.">
          Import CSV
          <input
            type="file"
            accept=".csv,text/csv,text/comma-separated-values"
            onChange={(e) => e.target.files[0] && importCSV(e.target.files[0])}
            className="hidden"
          />
        </label>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold"
        >
          + New Staff
        </button>
      </div>

      {showAddForm && (
        <StaffForm
          onSave={handleSave}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4">
        {staff.map((member) => (
          editingId === member.staff_id ? (
            <StaffForm
              key={member.staff_id}
              staff={member}
              onSave={handleSave}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <div key={member.staff_id} className="border border-gray-200 rounded p-4 bg-white">
              <div className="flex justify-between items-start mb-3">
                <div className="font-semibold text-base">{member.name}</div>
                <div className="flex flex-col gap-1">
                  {member.receive_live_call_notifications && (
                    <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 text-center">Live Call Alert</span>
                  )}
                  <span className={`px-2 py-1 rounded text-xs text-center ${member.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {member.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="space-y-1 text-sm text-gray-600 mb-3">
                <div><span className="font-medium">Email:</span> {member.email}</div>
                <div><span className="font-medium">Phone:</span> {member.phone}</div>
                {member.specialties && member.specialties.length > 0 && (
                  <div><span className="font-medium">Specialties:</span> {member.specialties.join(', ')}</div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setScheduleModalStaff(member)} className="bg-purple-500 hover:bg-purple-600 text-white py-2 rounded text-xs font-medium">
                  Availability
                </button>
                <button onClick={() => setEditingId(member.staff_id)} className="bg-blue-500 hover:bg-blue-600 text-white py-2 rounded text-xs font-medium">
                  Edit
                </button>
                <button onClick={() => handleDelete(member.staff_id)} className="bg-red-500 hover:bg-red-600 text-white py-2 rounded text-xs font-medium">
                  Delete
                </button>
              </div>
            </div>
          )
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block border border-gray-200 rounded bg-white overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold">Name</th>
              <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
              <th className="px-6 py-3 text-left text-sm font-semibold">Phone</th>
              <th className="px-6 py-3 text-left text-sm font-semibold">Status</th>
              <th className="px-6 py-3 text-left text-sm font-semibold">Availability</th>
              <th className="px-6 py-3 text-right text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((member) => (
              editingId === member.staff_id ? (
                <tr key={member.staff_id}>
                  <td colSpan="6" className="p-0">
                    <StaffForm
                      staff={member}
                      onSave={handleSave}
                      onCancel={() => setEditingId(null)}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={member.staff_id} className="border-t">
                  <td className="px-6 py-4">{member.name}</td>
                  <td className="px-6 py-4">{member.email}</td>
                  <td className="px-6 py-4">{member.phone}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      {member.receive_live_call_notifications && (
                        <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">📞 Live Call Alert</span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs ${member.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <button onClick={() => setScheduleModalStaff(member)} className="text-purple-500 hover:text-purple-700 text-sm font-medium">
                      Manage Schedule
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => setEditingId(member.staff_id)} className="text-blue-500 hover:text-blue-700 text-sm font-medium mr-4">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(member.staff_id)} className="text-red-500 hover:text-red-700 text-sm font-medium">
                      Delete
                    </button>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>

      {scheduleModalStaff && (
        <StaffScheduleModal
          staff={scheduleModalStaff}
          onClose={() => setScheduleModalStaff(null)}
        />
      )}
    </div>
  );
}

const StaffForm = ({ staff, onSave, onCancel }) => {
  const [countryCode, setCountryCode] = useState('+1');
  const [formData, setFormData] = useState({
    name: staff?.name || '',
    email: staff?.email || '',
    phone: staff?.phone || '',
    specialties: staff?.specialties?.join(', ') || '',
    receive_live_call_notifications: staff?.receive_live_call_notifications || false
  });

  useEffect(() => {
    if (staff && staff.phone) {
      const allDigits = staff.phone.replace(/\D/g, '');
      const countryCodeMap = {
        '1': { code: '+1', length: 1 },
        '44': { code: '+44', length: 2 },
        '33': { code: '+33', length: 2 },
        '49': { code: '+49', length: 2 },
        '39': { code: '+39', length: 2 },
        '34': { code: '+34', length: 2 },
        '61': { code: '+61', length: 2 },
        '81': { code: '+81', length: 2 }
      };
      
      let extractedCountryCode = '+1';
      let displayPhone = '';
      
      for (const [digits, info] of Object.entries(countryCodeMap)) {
        if (allDigits.startsWith(digits)) {
          extractedCountryCode = info.code;
          displayPhone = allDigits.slice(info.length);
          if (info.code === '+1' && displayPhone.length === 10) {
            displayPhone = `${displayPhone.slice(0, 3)}-${displayPhone.slice(3, 6)}-${displayPhone.slice(6)}`;
          }
          break;
        }
      }
      
      if (!displayPhone && allDigits.length === 10) {
        displayPhone = `${allDigits.slice(0, 3)}-${allDigits.slice(3, 6)}-${allDigits.slice(6)}`;
      }
      
      setCountryCode(extractedCountryCode);
      setFormData({
        name: staff.name || '',
        email: staff.email || '',
        phone: displayPhone,
        specialties: staff.specialties?.join(', ') || '',
        receive_live_call_notifications: staff.receive_live_call_notifications || false
      });
    }
  }, [staff]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const fullPhone = phoneDigits ? `${countryCode}${phoneDigits}` : '';
    onSave({
      ...formData,
      phone: fullPhone
    });
  };

  return (
    <div className="border border-gray-200 rounded p-4 bg-gray-50 mb-4">
      <h4 className="text-base font-medium text-gray-900 mb-4">{staff ? 'Edit' : 'Add'} Staff Member</h4>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Phone</label>
              <div className="flex gap-2">
                <select 
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900 flex-shrink-0"
                >
                  <option value="+1">{getFlagEmoji('US')} +1</option>
                  <option value="+1">{getFlagEmoji('CA')} +1</option>
                  <option value="+44">{getFlagEmoji('GB')} +44</option>
                  <option value="+33">{getFlagEmoji('FR')} +33</option>
                  <option value="+49">{getFlagEmoji('DE')} +49</option>
                  <option value="+39">{getFlagEmoji('IT')} +39</option>
                  <option value="+34">{getFlagEmoji('ES')} +34</option>
                  <option value="+61">{getFlagEmoji('AU')} +61</option>
                  <option value="+81">{getFlagEmoji('JP')} +81</option>
                </select>
                <input
                  type="tel"
                  placeholder="123-456-7890"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value;
                    const digits = value.replace(/\D/g, '');
                    
                    let formatted = digits.slice(0, 10);
                    if (formatted.length >= 6) {
                      formatted = `${formatted.slice(0, 3)}-${formatted.slice(3, 6)}-${formatted.slice(6)}`;
                    } else if (formatted.length >= 3) {
                      formatted = `${formatted.slice(0, 3)}-${formatted.slice(3)}`;
                    }
                    
                    setFormData({...formData, phone: formatted});
                  }}
                  className="flex-1 bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Specialties</label>
              <input
                type="text"
                placeholder="Comma-separated"
                value={formData.specialties}
                onChange={(e) => setFormData({...formData, specialties: e.target.value})}
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-900 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.receive_live_call_notifications}
                onChange={(e) => setFormData({...formData, receive_live_call_notifications: e.target.checked})}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>Receive Live Call Notifications</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">Get SMS alerts when calls come in</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="bg-white text-gray-600 border border-gray-200 px-4 py-2 rounded text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold"
          >
            {staff ? 'Update' : 'Add'} Staff
          </button>
        </div>
      </form>
    </div>
  );
};

export default StaffManager;
